import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Collapse,
  Divider,
  Alert,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Upload,
  Delete,
  Description,
  AutoAwesome,
  CheckCircle,
  Close,
  AttachFile,
  Image,
  Palette,
  Download,
  Visibility,
  Add,
  InsertDriveFile,
  PictureAsPdf,
  ChevronRight,
  ChevronLeft,
} from '@mui/icons-material';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import LogoCreator from './LogoCreator.tsx';
import SearchableDropdown from '../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import BrandPreferencesContent from './BrandPreferencesContent.tsx';
import LogoDesignForm from './LogoDesignForm.tsx';
import { useNavigate } from 'react-router';
import MachadaloStudioDashboard from './ComapnyForm.tsx';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CompanyDocument {
  id: string;
  name: string;
  description: string;
  category: string;
  subCategory: string;
  sector: string;
  subSector: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadedAt: string;
  isReference: boolean;
  createdDate?: string;
  relevance?: string;
  clientName?: string;
  status?: 'uploading' | 'uploaded' | 'error';
  uploadProgress?: number;
}

interface BrandPreferences {
  color_primary: string;
  color_accent: string;
  heading_font: string;
  body_font: string;
  title_size: number;
  body_size: number;
  slide_style: string;
  tone: string;
  logo_url?: string;
}

// ── Category Data ─────────────────────────────────────────────────────────────

interface CategoryData {
  value: string;
  label: string;
  icon: string;
  description: string;
  subCategories: string[];
}

const CATEGORIES_WITH_SUBCATEGORIES: CategoryData[] = [
  {
    value: 'overview',
    label: 'Overview',
    icon: '📋',
    description: 'Company overview, mission, vision, and core values',
    subCategories: [
      'Company Overview',
      'Mission Statement',
      'Vision Statement',
      'Core Values',
      'Company History',
      'Executive Summary',
      'Organizational Structure',
      'Culture Deck',
      'Company Profile',
      'About Us',
      'Leadership Team',
      'Board of Directors',
      'Company Timeline',
      'Annual Report Summary',
      'Business Model',
      'Value Proposition'
    ]
  },
  {
    value: 'logo_identity',
    label: 'Logo Identity',
    icon: '🏷️',
    description: 'Brand identity, logos, and visual guidelines',
    subCategories: [
      'Primary Logo',
      'Secondary Logo',
      'Logo Variations',
      'Logo Usage Guidelines',
      'Color Palette',
      'Typography',
      'Brand Guidelines',
      'Style Guide',
      'Icon Set',
      'Visual Identity',
      'Brand Book',
      'Logo History',
      'Brand Assets',
      'Logo Files',
      'Corporate Identity',
      'Visual Branding'
    ]
  },
  {
    value: 'core_functions_products',
    label: 'Core Functions & Products',
    icon: '⚡',
    description: 'Core business functions and product documentation',
    subCategories: [
      'Product Overview',
      'Product Specifications',
      'Product Roadmap',
      'Product Features',
      'Core Functions',
      'Service Offerings',
      'Product Catalog',
      'Solution Briefs',
      'Product Comparison',
      'Technical Specifications',
      'Product Architecture',
      'Feature List',
      'Product Demo',
      'Product Brochure',
      'Service Catalog',
      'Core Capabilities',
      'Product Strategy',
      'Product Lifecycle',
      'Product Documentation'
    ]
  },
  {
    value: 'sales',
    label: 'Sales',
    icon: '📊',
    description: 'Sales strategies, proposals, and client management',
    subCategories: [
      'Sales Pitch',
      'Sales Proposal',
      'Sales Deck',
      'Pricing Strategy',
      'Sales Pipeline',
      'Sales Playbook',
      'Competitive Analysis',
      'Market Positioning',
      'Client Presentations',
      'Case Studies',
      'Testimonials',
      'Lead Generation',
      'Sales Strategy',
      'Sales Training',
      'Sales Metrics',
      'Sales Enablement',
      'Client Success Stories',
      'Sales Process',
      'Sales Forecasting',
      'Go-to-Market Strategy',
      'Sales Collateral',
      'Partner Presentation',
      'Sales Scripts',
      'Objection Handling',
      'Win/Loss Analysis'
    ]
  },
  {
    value: 'hr',
    label: 'HR',
    icon: '👥',
    description: 'Human resources policies, recruitment, and employee management',
    subCategories: [
      'Recruitment Strategy',
      'Job Descriptions',
      'Interview Process',
      'Onboarding',
      'Training Material',
      'Employee Handbook',
      'Performance Reviews',
      'HR Policies',
      'Compensation',
      'Benefits Guide',
      'Employee Engagement',
      'Talent Management',
      'Recruitment Process',
      'HR Operations',
      'Employee Relations',
      'Workplace Culture',
      'Diversity & Inclusion',
      'Learning & Development',
      'HR Analytics',
      'Employee Wellness',
      'Talent Acquisition',
      'Retention Strategy',
      'Exit Interviews',
      'HR Compliance'
    ]
  },
  {
    value: 'operations_delivery',
    label: 'Operations & Delivery',
    icon: '⚙️',
    description: 'Operational processes, delivery frameworks, and quality management',
    subCategories: [
      'SOP Documentation',
      'Workflow Processes',
      'Quality Assurance',
      'Delivery Framework',
      'Project Management',
      'Resource Planning',
      'Supply Chain',
      'Inventory Management',
      'Service Delivery',
      'Operational Metrics',
      'Process Optimization',
      'Operations Strategy',
      'Delivery Excellence',
      'Operational Planning',
      'Quality Management',
      'Operational KPIs',
      'Standard Operating Procedures',
      'Business Continuity',
      'Risk Management',
      'Performance Management',
      'Process Mapping',
      'Value Stream Mapping',
      'Lean Operations',
      'Agile Delivery',
      'ITIL Processes'
    ]
  },
  {
    value: 'finance_accounting',
    label: 'Finance & Accounting',
    icon: '💰',
    description: 'Financial reports, budgeting, and accounting documents',
    subCategories: [
      'Financial Reports',
      'Budget Planning',
      'Forecasting',
      'Financial Statements',
      'Balance Sheet',
      'P&L Statements',
      'Cash Flow',
      'Audit Reports',
      'Tax Documents',
      'Compliance',
      'Billing & Invoicing',
      'Expense Reports',
      'Financial Planning',
      'Business Budget',
      'Revenue Forecast',
      'Financial Analysis',
      'Investment Reports',
      'Risk Assessment',
      'Financial Strategy',
      'Accounting Policies',
      'Financial Controls',
      'Cost Management',
      'Profitability Analysis',
      'Financial Projections',
      'Vendor Payment',
      'Receivables Management'
    ]
  },
  {
    value: 'it_systems',
    label: 'IT Systems',
    icon: '💻',
    description: 'IT infrastructure, systems architecture, and technical documentation',
    subCategories: [
      'System Architecture',
      'Technical Documentation',
      'API Documentation',
      'Security Policies',
      'IT Infrastructure',
      'Data Flow Diagrams',
      'Network Design',
      'IT Compliance',
      'Software Development',
      'System Integrations',
      'DevOps',
      'IT Strategy',
      'IT Roadmap',
      'Technical Specifications',
      'Data Architecture',
      'Cloud Strategy',
      'Cybersecurity',
      'IT Operations',
      'Disaster Recovery',
      'System Administration',
      'Database Design',
      'Application Architecture',
      'IT Audit',
      'Technology Stack',
      'Software Licenses',
      'IT Governance',
      'Data Privacy',
      'Service Level Agreements'
    ]
  },
  {
    value: 'legal',
    label: 'Legal',
    icon: '⚖️',
    description: 'Legal documents, contracts, and compliance documentation',
    subCategories: [
      'Contracts',
      'NDA Agreements',
      'Partnership Agreements',
      'Compliance Documents',
      'Legal Policies',
      'Terms of Service',
      'Privacy Policy',
      'Licensing',
      'Regulatory Documents',
      'Legal Opinions',
      'Intellectual Property',
      'Risk Management',
      'Employment Contracts',
      'Service Agreements',
      'Vendor Contracts',
      'Legal Compliance',
      'Corporate Governance',
      'Data Protection',
      'GDPR Compliance',
      'Legal Framework',
      'Dispute Resolution',
      'Contract Management',
      'Due Diligence',
      'Legal Research',
      'Regulatory Filings'
    ]
  },
  {
    value: 'research_development',
    label: 'Research & Development',
    icon: '🔬',
    description: 'R&D activities, research papers, and innovation documentation',
    subCategories: [
      'Research Reports',
      'Market Research',
      'Competitor Research',
      'User Research',
      'Product Innovation',
      'R&D Roadmap',
      'Technical Research',
      'Feasibility Studies',
      'Prototype Development',
      'Test Results',
      'Research Papers',
      'Patent Documents',
      'R&D Strategy',
      'Innovation Pipeline',
      'Technology Research',
      'Scientific Papers',
      'White Papers',
      'Research Methodology',
      'Research Findings',
      'IP Strategy',
      'Research Proposals',
      'Technical Reports',
      'Innovation Management',
      'Product Testing',
      'Quality Research'
    ]
  },
  {
    value: 'others',
    label: 'Others',
    icon: '📁',
    description: 'Miscellaneous and uncategorized documents',
    subCategories: [
      'Miscellaneous',
      'General Documents',
      'Reference Material',
      'Archive',
      'External Resources',
      'Third-party Documents',
      'Drafts',
      'Work in Progress',
      'Templates',
      'Internal Memos',
      'Project Archive',
      'Historical Documents',
      'External Reference',
      'Cross-functional Documents',
      'Shared Resources',
      'General Reference',
      'Administrative Documents'
    ]
  }
];

// Category color mapping
const CATEGORY_COLORS: Record<string, string> = {
  'overview': '#6366F1',
  'logo_identity': '#8B5CF6',
  'core_functions_products': '#3B82F6',
  'sales': '#F59E0B',
  'hr': '#EC4899',
  'operations_delivery': '#14B8A6',
  'finance_accounting': '#10B981',
  'it_systems': '#06B6D4',
  'legal': '#EF4444',
  'research_development': '#8B5CF6',
  'others': '#6B7280',
};

// ── Helper Functions ──────────────────────────────────────────────────────────

const getCategoryColor = (value: string): string => {
  return CATEGORY_COLORS[value] || '#6B7280';
};

const getCategoryLabel = (value: string): string => {
  const category = CATEGORIES_WITH_SUBCATEGORIES.find(c => c.value === value);
  return category?.label || value;
};

const getSubCategories = (categoryValue: string): string[] => {
  const category = CATEGORIES_WITH_SUBCATEGORIES.find(c => c.value === categoryValue);
  return category?.subCategories || [];
};

// ── Main Component ────────────────────────────────────────────────────────────
export const SETUP_TABS = ['details', 'documents', 'logo', 'brand'] as const;

interface CompanySetupPageProps {
  companyId: string;
  companyName: string;
  onComplete: (data: {
    documents: CompanyDocument[];
    brandPreferences: BrandPreferences;
  }) => void;
  onSkip: () => void;
  initialDocuments?: CompanyDocument[];
  initialBrand?: BrandPreferences;
  onOpenCompanyForm?: () => void;
  activeStep?: number;
  onStepChange?: (step: number) => void;
}
interface UploadFileItem {
  file: File;
  label: string;
  description: string;
  category: string;
  subCategory: string;
}

const CompanySetupPage: React.FC<CompanySetupPageProps> = (props) => {
  const {
    companyId,
    companyName,
    onComplete,
    onSkip,
    initialDocuments = [],
    initialBrand = null,
    onOpenCompanyForm,
    activeStep: activeStepProp,
    onStepChange,
  } = props;
  const { showSnackBar } = useSnackBar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────────────────

  // const [activeStep, setActiveStep] = useState(0);
  const [internalStep, setInternalStep] = useState(0);
  const activeStep = activeStepProp ?? internalStep;
  const setActiveStep = (step: number) => {
    if (onStepChange) {
      onStepChange(step);
    } else {
      setInternalStep(step);
    }
  };
  const [documents, setDocuments] = useState<CompanyDocument[]>(initialDocuments);
  const [brandPreferences, setBrandPreferences] = useState<BrandPreferences>(
    initialBrand || {
      color_primary: '#4962E5',
      color_accent: '#7C3AED',
      heading_font: 'Poppins',
      body_font: 'Inter',
      title_size: 42,
      body_size: 16,
      slide_style: 'Modern',
      tone: 'Professional',
      logo_url: '',
    }
  );

  // Document upload state
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [uploadFiles, setUploadFiles] = useState<UploadFileItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadArea, setShowUploadArea] = useState(false);
  const [expandedDocId, setExpandedDocId] = useState<string | null>(null);

  // Logo state
  const [showLogoCreator, setShowLogoCreator] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(brandPreferences?.logo_url || null);

  // Brand analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [brandSuggestions, setBrandSuggestions] = useState<any>(null);
  const [brandSaved, setBrandSaved] = useState(!!initialBrand)

  // UI state
  const [error, setError] = useState<string | null>(null);

  // ── Computed ─────────────────────────────────────────────────────────────────

  const hasDocuments = documents.length > 0;
  const hasLogo = !!logoPreview || !!brandPreferences?.logo_url;
  const hasBrand = brandSaved;

  // ── Document Handlers ───────────────────────────────────────────────────────
  const categoryOptions = CATEGORIES_WITH_SUBCATEGORIES.map(c => ({
    label: `${c.icon} ${c.label}`,
    value: c.value,
  }));

  const getSubCategoryOptions = (categoryValue: string) =>
    getSubCategories(categoryValue).map(sub => ({ label: sub, value: sub }));

  const updateFileCategory = (index: number, categoryValue: string) => {
    setUploadFiles(prev =>
      prev.map((item, idx) =>
        idx === index ? { ...item, category: categoryValue, subCategory: '' } : item
      )
    );
  };

  const updateFileSubCategory = (index: number, subCategoryValue: string) => {
    setUploadFiles(prev =>
      prev.map((item, idx) => (idx === index ? { ...item, subCategory: subCategoryValue } : item))
    );
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const newItems: UploadFileItem[] = files.map((file) => ({
      file,
      label: file.name.replace(/\.[^.]+$/, ''),
      description: '',
      category: selectedCategory || '',
      subCategory: selectedSubCategory || '',
    }));
    setUploadFiles(prev => [...prev, ...newItems]);
    event.target.value = '';
    setError(null);
  };

  const removeSelectedFile = (index: number) => {
    setUploadFiles(prev => prev.filter((_, idx) => idx !== index));
  };

  const updateFileMeta = (index: number, field: 'label' | 'description', value: string) => {
    setUploadFiles(prev =>
      prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item))
    );
  };

  const simulateUpload = (file: File): Promise<{ url: string }> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ url: reader.result as string });
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async () => {
    if (!uploadFiles.length) {
      setError('Please select at least one file');
      return;
    }
    if (!selectedCategory) {
      setError('Please select a category');
      return;
    }

    setIsUploading(true);
    setError(null);

    for (let i = 0; i < uploadFiles.length; i++) {
      const item = uploadFiles[i];
      const docId = `doc-${Date.now()}-${i}`;

      const tempDoc: CompanyDocument = {
        id: docId,
        name: item.label || item.file.name.replace(/\.[^.]+$/, ''),
        description: item.description || '',
        category: item.category || selectedCategory,
        subCategory: item.subCategory || selectedSubCategory || '',
        sector: selectedCategory,
        subSector: selectedSubCategory || '',
        fileUrl: '',
        fileName: item.file.name,
        fileSize: item.file.size,
        fileType: item.file.type || 'application/octet-stream',
        uploadedAt: new Date().toISOString(),
        isReference: false,
        status: 'uploading',
        uploadProgress: 0,
      };

      setDocuments(prev => [...prev, tempDoc]);

      for (let progress = 10; progress <= 100; progress += 30) {
        await new Promise(r => setTimeout(r, 80));
        setDocuments(prev => prev.map(d =>
          d.id === docId ? { ...d, uploadProgress: progress } : d
        ));
      }

      const result = await simulateUpload(item.file);
      setDocuments(prev => prev.map(d =>
        d.id === docId ? { ...d, fileUrl: result.url, status: 'uploaded', uploadProgress: 100 } : d
      ));
    }

    setSelectedCategory('');
    setSelectedSubCategory('');
    setUploadFiles([]);
    setShowUploadArea(false);
    setIsUploading(false);

    showSnackBar(`Uploaded ${uploadFiles.length} document(s)`, 'success');

    if (documents.length + uploadFiles.length >= 2 && !brandSuggestions) {
      setTimeout(() => handleAnalyze(), 500);
    }
  };
  const handleDeleteDocument = (docId: string) => {
    setDocuments(prev => prev.filter(d => d.id !== docId));
  };

  const toggleExpand = (docId: string) => {
    setExpandedDocId(expandedDocId === docId ? null : docId);
  };

  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return <PictureAsPdf sx={{ color: '#DC2626', fontSize: 18 }} />;
    if (type.includes('presentation') || type.includes('ppt')) return <Description sx={{ color: '#D97706', fontSize: 18 }} />;
    if (type.includes('word') || type.includes('doc')) return <Description sx={{ color: '#2563EB', fontSize: 18 }} />;
    if (type.includes('image')) return <Image sx={{ color: '#7C3AED', fontSize: 18 }} />;
    return <InsertDriveFile sx={{ color: '#6B7280', fontSize: 18 }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // ── Logo Handlers ───────────────────────────────────────────────────────────
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setLogoPreview(url);
      setBrandPreferences(prev => ({ ...prev, logo_url: url }));
      showSnackBar('Logo uploaded successfully!', 'success');
    };
    reader.readAsDataURL(file);
  };

  // ── Brand Handlers ──────────────────────────────────────────────────────────
  const handleAnalyze = async () => {
    if (!hasDocuments) return;

    setIsAnalyzing(true);
    try {
      await new Promise(r => setTimeout(r, 1500));

      // Analyze based on document categories
      const hasSales = documents.some(d => d.category === 'sales');
      const hasFinance = documents.some(d => d.category === 'finance_accounting');
      const hasLogo = documents.some(d => d.category === 'logo_identity');
      const hasRnd = documents.some(d => d.category === 'research_development');
      const hasOperations = documents.some(d => d.category === 'operations_delivery');
      const hasIT = documents.some(d => d.category === 'it_systems');
      const hasLegal = documents.some(d => d.category === 'legal');

      let primaryColor = '#4962E5';
      let accentColor = '#7C3AED';
      let tone = 'Professional';
      let style = 'Modern';

      if (hasSales) {
        primaryColor = '#F59E0B';
        accentColor = '#8B5CF6';
        tone = 'Professional';
        style = 'Corporate';
      } else if (hasFinance) {
        primaryColor = '#10B981';
        accentColor = '#1E293B';
        tone = 'Formal';
        style = 'Executive';
      } else if (hasLogo) {
        primaryColor = '#8B5CF6';
        accentColor = '#EC4899';
        tone = 'Creative';
        style = 'Modern';
      } else if (hasRnd) {
        primaryColor = '#3B82F6';
        accentColor = '#06B6D4';
        tone = 'Innovative';
        style = 'Modern';
      } else if (hasOperations) {
        primaryColor = '#14B8A6';
        accentColor = '#1E293B';
        tone = 'Professional';
        style = 'Corporate';
      } else if (hasIT) {
        primaryColor = '#06B6D4';
        accentColor = '#3B82F6';
        tone = 'Technical';
        style = 'Modern';
      } else if (hasLegal) {
        primaryColor = '#1E293B';
        accentColor = '#EF4444';
        tone = 'Formal';
        style = 'Executive';
      }

      const suggestions = {
        color_primary: primaryColor,
        color_accent: accentColor,
        heading_font: 'Poppins',
        body_font: 'Inter',
        title_size: 42,
        body_size: 16,
        slide_style: style,
        tone: tone,
        confidence: 0.85,
      };

      setBrandSuggestions(suggestions);
      showSnackBar('AI analyzed your documents!', 'success');
    } catch (error) {
      showSnackBar('Analysis failed', 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };
  // ── Complete ────────────────────────────────────────────────────────────────
  const handleComplete = () => {
    if (!hasDocuments) {
      setError('Please upload at least one document');
      return;
    }
    if (!hasLogo) {
      setError('Please add a company logo');
      return;
    }
    onComplete({
      documents,
      brandPreferences,
    });
    navigate("/r/machadalostudio/ai");
  };

  // ── Render: Document Chips ──────────────────────────────────────────────────

  const renderDocumentChips = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {documents.map(doc => {
        const isUploading = doc.status === 'uploading';
        const isExpanded = expandedDocId === doc.id;
        const progress = doc.uploadProgress || 0;
        const categoryColor = getCategoryColor(doc.category);

        return (
          <Box key={doc.id} sx={{ position: 'relative', display: 'inline-flex' }}>
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {getFileIcon(doc.fileType)}
                  <Typography variant="caption" sx={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.name}
                  </Typography>
                  <Chip
                    label={getCategoryLabel(doc.category)}
                    size="small"
                    sx={{
                      height: 14,
                      fontSize: 8,
                      bgcolor: categoryColor,
                      color: '#fff',
                      '& .MuiChip-label': { px: 0.5 }
                    }}
                  />
                  {isUploading && (
                    <CircularProgress size={12} variant="determinate" value={progress} sx={{ ml: 0.5 }} />
                  )}
                </Box>
              }
              size="small"
              variant={isExpanded ? 'filled' : 'outlined'}
              color={isExpanded ? 'primary' : 'default'}
              onClick={() => !isUploading && toggleExpand(doc.id)}
              onDelete={() => !isUploading && handleDeleteDocument(doc.id)}
              deleteIcon={!isUploading ? <Delete sx={{ fontSize: 14 }} /> : undefined}
              sx={{ mb: 0.5 }}
            />
            {isExpanded && (
              <Box
                sx={{
                  position: 'absolute',
                  zIndex: 10,
                  top: '100%',
                  left: 0,
                  mt: 0.5,
                  bgcolor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 1,
                  p: 1,
                  minWidth: 200,
                  maxWidth: 260,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                <Typography variant="caption" display="block" fontWeight={600}>{doc.name}</Typography>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: 10 }}>
                  {doc.description || 'No description'}
                </Typography>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={getCategoryLabel(doc.category)}
                    size="small"
                    sx={{ fontSize: 9, height: 18, bgcolor: getCategoryColor(doc.category), color: '#fff' }}
                  />
                  {doc.subCategory && (
                    <Chip label={doc.subCategory} size="small" variant="outlined" sx={{ fontSize: 9, height: 18 }} />
                  )}
                </Box>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: 10 }}>
                  {formatFileSize(doc.fileSize)} · {new Date(doc.uploadedAt).toLocaleDateString()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  <IconButton size="small" onClick={() => window.open(doc.fileUrl, '_blank')}>
                    <Visibility sx={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton size="small">
                    <Download sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );

  // ── Render: Upload Area ────────────────────────────────────────────────────

  const renderUploadArea = () => (
    <Collapse in={showUploadArea}>
      <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px dashed #d1d5db' }}>
        {/* Category Chips */}
        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
          Category *
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {CATEGORIES_WITH_SUBCATEGORIES.map(c => (
            <Chip
              key={c.value}
              label={c.label}
              size="small"
              variant={selectedCategory === c.value ? 'filled' : 'outlined'}
              color={selectedCategory === c.value ? 'primary' : 'default'}
              onClick={() => {
                setSelectedCategory(c.value);
                setSelectedSubCategory('');
              }}
              icon={<Typography sx={{ fontSize: 12 }}>{c.icon}</Typography>}
              sx={{
                borderColor: selectedCategory === c.value ? getCategoryColor(c.value) : undefined,
                bgcolor: selectedCategory === c.value ? getCategoryColor(c.value) : undefined,
                color: selectedCategory === c.value ? '#fff' : undefined,
                '&:hover': {
                  bgcolor: selectedCategory === c.value ? getCategoryColor(c.value) : undefined,
                }
              }}
            />
          ))}
        </Box>

        {/* Sub-Category Chips */}
        {selectedCategory && (
          <>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
              Sub-Category
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {getSubCategories(selectedCategory).map(sub => (
                <Chip
                  key={sub}
                  label={sub}
                  size="small"
                  variant={selectedSubCategory === sub ? 'filled' : 'outlined'}
                  color={selectedSubCategory === sub ? 'secondary' : 'default'}
                  onClick={() => setSelectedSubCategory(selectedSubCategory === sub ? '' : sub)}
                />
              ))}
            </Box>
          </>
        )}

        {/* File Selection */}
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AttachFile sx={{ fontSize: 16 }} />}
            onClick={() => fileInputRef.current?.click()}
          >
            Select Files
          </Button>
          {uploadFiles.length > 0 && (
            <Chip
              label={`${uploadFiles.length} file(s)`}
              size="small"
              color="info"
              onDelete={() => setUploadFiles([])}
            />
          )}
          <Button
            size="small"
            variant="contained"
            onClick={handleUpload}
            disabled={!uploadFiles.length || !selectedCategory || isUploading}
          >
            {isUploading ? <CircularProgress size={14} /> : 'Upload'}
          </Button>
          <Button size="small" onClick={() => setShowUploadArea(false)}>Cancel</Button>
        </Box>

        {uploadFiles.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
            {uploadFiles.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  p: 1.2,
                  border: '1px solid #e5e7eb',
                  borderRadius: 1.5,
                  bgcolor: '#fff',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                  <Typography variant="caption" fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.file.name}
                  </Typography>
                  <IconButton size="small" onClick={() => removeSelectedFile(idx)}>
                    <Close sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>

                <TextField
                  label="File Label *"
                  size="small"
                  fullWidth
                  value={item.label}
                  onChange={(e) => updateFileMeta(idx, 'label', e.target.value)}
                  sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: 13 } }}
                />

                <TextField
                  label="Description"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  value={item.description}
                  onChange={(e) => updateFileMeta(idx, 'description', e.target.value)}
                  sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
                />

                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <SearchableDropdown
                    label="Category"
                    options={categoryOptions}
                    labelKey="label"
                    valueKey="value"
                    value={categoryOptions.find(o => o.value === item.category) || null}
                    onChange={(val) => updateFileCategory(idx, val?.value || '')}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <SearchableDropdown
                    label="Sub-Category"
                    options={getSubCategoryOptions(item.category)}
                    labelKey="label"
                    valueKey="value"
                    value={item.subCategory ? { label: item.subCategory, value: item.subCategory } : null}
                    onChange={(val) => updateFileSubCategory(idx, val?.value || '')}
                    disabled={!item.category}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={handleFileSelection}
        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.jpg,.jpeg,.png,.svg"
      />
    </Collapse>
  );

  // ── Render: Logo Section ───────────────────────────────────────────────────

  const renderLogoSection = () => (
    <Box>
      {hasLogo ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#fff',
              flexShrink: 0,
            }}
          >
            <img
              src={logoPreview || brandPreferences.logo_url}
              alt="Logo"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
          <Chip label="✓ Logo added" size="small" color="success" icon={<CheckCircle sx={{ fontSize: 14 }} />} />
          <Button size="small" variant="outlined" onClick={() => setShowLogoCreator(true)}>
            Change Logo
          </Button>
          <Button size="small" onClick={() => { setLogoPreview(null); setBrandPreferences(prev => ({ ...prev, logo_url: '' })); }}>
            Remove
          </Button>
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<AutoAwesome sx={{ fontSize: 16 }} />}
              onClick={() => setShowLogoCreator(true)}
            >
              Create with AI
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Upload sx={{ fontSize: 16 }} />}
              component="label"
            >
              Upload Logo
              <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
            </Button>
          </Box>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
            PNG, JPG, SVG supported
          </Typography>
        </Box>
      )}

      {/* Logo Creator - inline, not modal */}
      {showLogoCreator && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            border: '1px solid #e5e7eb',
            borderRadius: 1,
            bgcolor: '#fafafa',
          }}
        >
          <LogoCreator
            companyId={companyId}
            brandColors={[brandPreferences.color_primary, brandPreferences.color_accent]}
            brandTone={brandPreferences.tone}
            onLogoCreated={(url) => {
              setLogoPreview(url);
              setBrandPreferences(prev => ({ ...prev, logo_url: url }));
              setShowLogoCreator(false);
              showSnackBar('Logo created successfully!', 'success');
            }}
            onCancel={() => setShowLogoCreator(false)}
            initialLogo={logoPreview || brandPreferences.logo_url}
          />
        </Box>
      )}
      <LogoDesignForm
        onGenerate={(data) => {
          console.log(data);

          // API call
          // generateLogo(data)
        }}
      />
    </Box>
  );

  // ── Render: Brand Section ──────────────────────────────────────────────────

  const renderBrandSection = () => (
    <Box sx={{ mt: 1 }}>
      <BrandPreferencesContent
        companyName={companyName}
        initialBrand={brandPreferences}
        onCancel={() => setActiveStep(1)}
        onReset={() => setBrandSaved(false)}
        onSave={(updatedBrand) => {
          setBrandPreferences(prev => ({ ...prev, ...updatedBrand }));
          setBrandSaved(true);
          showSnackBar('Brand preferences saved!', 'success');
        }}
      />
    </Box>
  );

  // ── Step Indicators ────────────────────────────────────────────────────────

  const steps = [
    { label: 'Company Details', icon: <Description sx={{ fontSize: 16 }} />, },
    { label: 'Company Documents', icon: <Description sx={{ fontSize: 16 }} />, complete: hasDocuments },
    { label: 'Logo', icon: <Image sx={{ fontSize: 16 }} />, complete: hasLogo },
    { label: 'Brand', icon: <Palette sx={{ fontSize: 16 }} />, complete: hasBrand },
  ];

  // ── Main Render ─────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: '100%' }}>
      {/* Step Indicators */}
      <Tabs
        value={activeStep}
        onChange={(_, newValue) => setActiveStep(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 1.5,
          minHeight: 36,
          borderBottom: '1px solid #e5e7eb',
          '& .MuiTabs-indicator': { height: 2 },
        }}
      >
        {steps.map((step, idx) => (
          <Tab
            key={idx}
            value={idx}
            label={step.label}
            icon={step.complete ? <CheckCircle sx={{ fontSize: 16 }} /> : step.icon}
            iconPosition="start"
            sx={{
              minHeight: 36,
              py: 0.5,
              textTransform: 'none',
              fontSize: 13,
              fontWeight: idx === activeStep ? 600 : 400,
              color: step.complete ? '#2E7D32 !important' : undefined,
            }}
          />
        ))}
      </Tabs>

      {/* Step Content */}
      <Box sx={{ minHeight: 120 }}>
        {activeStep === 0 && (
          <Box>
            <MachadaloStudioDashboard onComplete={() => setActiveStep(1)} />
          </Box>
        )}

        {/* Step 1: Documents */}
        {activeStep === 1 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
              {hasDocuments ? (
                renderDocumentChips()
              ) : (
                <Typography variant="caption" color="textSecondary">No documents uploaded</Typography>
              )}
              <Chip
                label="Add"
                size="small"
                icon={<Add sx={{ fontSize: 14 }} />}
                onClick={() => setShowUploadArea(!showUploadArea)}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
            {renderUploadArea()}
            {hasDocuments && (
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                <Button size="small" variant="contained" endIcon={<ChevronRight />} onClick={() => setActiveStep(2)}>
                  Next: Logo
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Step 2: Logo */}
        {activeStep === 2 && (
          <Box>
            {renderLogoSection()}
            <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5 }}>
              <Button size="small" startIcon={<ChevronLeft />} onClick={() => setActiveStep(1)}>
                Back
              </Button>
              <Button size="small" variant="contained" endIcon={<ChevronRight />} onClick={() => setActiveStep(3)} disabled={!hasLogo}>
                Next: Brand
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Brand */}
        {activeStep === 3 && (
          <Box>
            {renderBrandSection()}
            <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Button size="small" startIcon={<ChevronLeft />} onClick={() => setActiveStep(2)}>
                Back
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={handleComplete}
                startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
              >
                Enter Workspace
              </Button>
              <Button size="small" onClick={onSkip}>
                Skip
              </Button>
              {onOpenCompanyForm && (
                <Button size="small" variant="outlined" onClick={onOpenCompanyForm}>
                  Edit Company Details
                </Button>
              )}
            </Box>
            {error && (
              <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CompanySetupPage;